import type { DiscordSDK } from '@discord/embedded-app-sdk';
import { IdentitySource } from '@infiltre/shared';
import { type Api, createApi } from '@/shared/lib/api';
import { ensureDiscordSdk } from '../discord/sdk';
import type { Identity, IdentityProvider } from './types';
import { clearWebToken, getWebToken, setWebToken } from './web-token';

/** Minimal scope — broader would prompt consent for no gameplay benefit. */
const SCOPES = ['identify'] as const;

const formatErr = (err: { value: unknown }): string => {
  const v = err.value;
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object' && 'error' in v && typeof v.error === 'string') return v.error;
  return JSON.stringify(v);
};

const readClientId = (): string => {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_DISCORD_CLIENT_ID ?? '';
};

/**
 * Discord Activity identity. SDK ready → authorize(identify) → POST `/auth/discord`
 * → mint HMAC bearer. Discord token never leaves; gameplay uses our bearer only.
 */
export const createDiscordIdentityProvider = (apiBaseUrl: string): IdentityProvider => {
  let api: Api | null = null;

  const ensureSdk = async (): Promise<DiscordSDK> => {
    const { sdk } = await ensureDiscordSdk(readClientId());
    return sdk;
  };

  const getApi = (): Api => {
    if (!api) api = createApi(apiBaseUrl, getWebToken);
    return api;
  };

  const acquire = async (): Promise<Identity> => {
    const s = await ensureSdk();
    const clientId = readClientId();
    const { code } = await s.commands.authorize({
      client_id: clientId,
      response_type: 'code',
      state: '',
      prompt: 'none',
      scope: [...SCOPES],
    });
    const res = await getApi().auth.discord.post({ code });
    if (res.error) throw new Error(`auth_discord_failed: ${formatErr(res.error)}`);
    const data = res.data as { token: string; playerId: string; name: string };
    setWebToken(data.token);
    // Best-effort iframe authenticate; failure harmless (we use HMAC bearer).
    try {
      await s.commands.authenticate({ access_token: data.token });
    } catch {
      // iframe handshake harmless on failure — HMAC bearer is the auth source
    }
    return { playerId: data.playerId, name: data.name, source: IdentitySource.Discord };
  };

  const restore = async (): Promise<Identity | null> => {
    if (!getWebToken()) return null;
    try {
      const res = await getApi().auth.me.get();
      if (res.error) {
        clearWebToken();
        return null;
      }
      const data = res.data as { playerId: string; name: string; source: IdentitySource } | null;
      if (!data) {
        clearWebToken();
        return null;
      }
      return { playerId: data.playerId, name: data.name, source: data.source };
    } catch {
      clearWebToken();
      return null;
    }
  };

  const clear = async (): Promise<void> => {
    clearWebToken();
  };

  return {
    acquire,
    restore,
    clear,
    getToken: getWebToken,
    setToken: setWebToken,
    source: IdentitySource.Discord,
  };
};
